import cn from 'classnames'
import React, { useCallback, useEffect, useState } from 'react'

import { getShellName } from 'components/Editor/EditorContext/selectors/blockLabels'
import EditorElementComponent from 'components/Editor/EditorElements/EditorElementComponent'
import CanvasComment from 'components/comments/EditorCommentBubble'
import CanvasCreateComment from 'components/comments/EditorCreateComment'
import { scrollProps } from 'services/Scroll/ScrollService'
import { setCommentForm, setHighlightPreview } from 'services/Store/Project/actions'
import { AppMode, PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import {
  getEditorMode,
  getIsCommentedElement,
  getIsCommenting,
  getIsOpenComment,
  getIsPreviewHighlighted,
} from 'services/Store/Project/selectors'
import { useElementViewed } from 'services/lms/hooks'

import type { IShellProps } from '../../Node/types'
import ControlSelection from '../../controls/ControlSelection'
import * as s from './PreviewShell.module.scss'

const PreviewShell: React.FC<IShellProps> = ({ mode, block, style, wrapperStyle, node }) => {
  const { uuid: blockId } = block
  const { id: nodeId, type, elementId } = node
  const [highlight, setHighlight] = useState(false)
  const editorMode = useProjectContext(getEditorMode)
  const dispatch = useProjectDispatch()
  const shellCn = cn(s.shell, s[type], {
    [s.pdf]: mode.previewMode === PreviewMode.pdf,
  })
  const label = getShellName(block, nodeId)
  const isCommented = useProjectContext(getIsCommentedElement, elementId)
  const isCommenting = useProjectContext(getIsCommenting, blockId, elementId)
  const isOpenedComment = useProjectContext(getIsOpenComment, { blockId, nodeId, elementId })
  const isHighlight = useProjectContext(getIsPreviewHighlighted, elementId || '')

  const { refElViewed } = useElementViewed({
    element: elementId ? block.elements[elementId] : null,
    block,
    mode,
  })

  const onMouseEnter = () => setHighlight(true)
  const onMouseLeave = () => setHighlight(false)

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (elementId && mode.editorMode === AppMode.comment) {
        e.stopPropagation()
        dispatch(setCommentForm({ blockId: blockId, elementId: elementId }))
      }
    },
    [dispatch, blockId, mode.editorMode, elementId],
  )

  const cnRoot = cn(s.wrapper, {
    [s.zeroLineHeight]: elementId && block.elements[elementId]?.type === 'image',
  })

  useEffect(() => {
    if (!elementId || !isHighlight) {
      return
    }

    const timeout = setTimeout(() => {
      dispatch(setHighlightPreview(null))
    }, 1000)

    return () => clearTimeout(timeout)
  }, [isHighlight, elementId, dispatch])

  return (
    <div className={cnRoot} style={wrapperStyle} {...scrollProps(nodeId)}>
      <div
        className={shellCn}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        ref={refElViewed}
        style={style}
      >
        {elementId && block.elements[elementId] && (
          <EditorElementComponent
            block={block}
            element={block.elements[elementId]}
            mode={mode}
            shellStyles={style}
          />
        )}
        {isCommented && mode.editorMode === AppMode.comment && (
          <CanvasComment blockId={blockId} elementId={elementId} nodeId={nodeId} />
        )}
        {isCommenting && <CanvasCreateComment blockId={blockId} elementId={elementId} />}
        {((highlight && editorMode === AppMode.comment) || isCommenting || isOpenedComment) && (
          <ControlSelection
            active={false}
            highlighted={highlight || isOpenedComment || isCommenting}
            label={label}
            name='shellSelection'
          />
        )}
      </div>
      <div className={cn(s.overlay, { [s.overlayHidden]: !isHighlight })} aria-hidden />
    </div>
  )
}

export default React.memo(PreviewShell) as typeof PreviewShell
